import {
  ActionLink,
  BoldTextM,
  Breakpoint,
  CheckCircleFilledIcon,
  CloseCircleIcon,
  HorizontalFlex,
  IconSize,
  Loader,
  LoaderSize,
  ToastVariant,
} from "@enxcs/dap-ui-stratos";
import { FC, MouseEventHandler, ReactElement, useEffect } from "react";
import { createPortal } from "react-dom";
import { ToastCloseButton, ToastIcon, ToastRoot, ToastText } from "./Toast.styles";

export interface ToastProps {
  text: string;
  variant: ToastVariant;
  linkLabel?: string;
  closeTitle: string;
  className?: string;
  autoClose?: boolean;
  onLinkClick?: MouseEventHandler<HTMLButtonElement>;
  onClose: () => void;
}

const Toast: FC<ToastProps> = ({ text, variant, linkLabel, closeTitle, autoClose, className, onLinkClick, onClose }) => {
  // Create a timer which close the toast after a delay
  useEffect(() => {
    if (variant === ToastVariant.PROGRESS || autoClose === false) {
      return;
    }

    const timer = window.setTimeout(onClose, 5 * 1000);
    return () => clearTimeout(timer);
  }, [autoClose, variant, text, onClose]);

  const iconMap: Record<ToastVariant, ReactElement> = {
    [ToastVariant.PROGRESS]: <Loader size={LoaderSize.V18} />,
    [ToastVariant.ERROR]: <CloseCircleIcon size={IconSize.V18} />,
    [ToastVariant.SUCCESS]: <CheckCircleFilledIcon size={IconSize.V18} />,
    [ToastVariant.WARNING]: <CloseCircleIcon size={IconSize.V18} />,
  };

  return createPortal(
    <ToastRoot $variant={variant} className={className} role="alertdialog">
      <ToastIcon>{iconMap[variant]}</ToastIcon>

      <HorizontalFlex $justify="space-between" $breakpoint={Breakpoint.L} $grow>
        <ToastText title={text} data-qa="toast-text">
          {text}
        </ToastText>

        {linkLabel && (
          <BoldTextM $shrink>
            {linkLabel && onLinkClick && (
              <BoldTextM $shrink>
                <ActionLink label={linkLabel} qa="toast-link" onClick={onLinkClick} />
              </BoldTextM>
            )}
          </BoldTextM>
        )}
      </HorizontalFlex>

      <ToastCloseButton title={closeTitle} size={IconSize.V18} qa="toast-close" onClick={onClose} />
    </ToastRoot>,
    window.document.body,
  );
};

export default Toast;
